// QAView — Wave 1 final: gamified Q&A studio for every published lesson.
//
// ┌─ Teacher Q&A ────────────────────────────────────────────────────────┐
// │  scope tabs  · This Lesson  |  All Class                             │
// │  expandable Q ▸ A list (groups by lesson when scope=all)             │
// └──────────────────────────────────────────────────────────────────────┘
// ┌─ AI Quiz Game ───────────────────────────────────────────────────────┐
// │  setup → one-card-at-a-time playthrough with streak / combo / XP     │
// │  → score reveal with confetti + badge unlocks + per-Q feedback       │
// └──────────────────────────────────────────────────────────────────────┘
// ┌─ Quiz Badges ────────────────────────────────────────────────────────┐
// │  earned grid + next-badge progress bars (scales with any topic)      │
// └──────────────────────────────────────────────────────────────────────┘
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  GraduationCap, Sparkles, Play, ChevronRight, Check, X as XIcon, RefreshCw,
  BookOpen, Trophy, Zap, Target, ChevronLeft,
} from 'lucide-react';
import api from '../../studentApi';
import { toast } from 'sonner';
import { MathText } from '../../../../components/RenderContent';

const HARDNESS = [
  { id: 'easy',   label: 'Easy',   desc: 'Recall & recognition.',     mult: 1.0 },
  { id: 'medium', label: 'Medium', desc: 'Apply & interpret.',        mult: 1.25 },
  { id: 'hard',   label: 'Hard',   desc: 'Analyse & multi-step.',     mult: 1.6 },
];
const STYLES = [
  { id: 'mcq',   label: 'Multiple choice' },
  { id: 'short', label: 'Short answer' },
  { id: 'mixed', label: 'Mixed' },
];

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return ''; }
};

const fmtDateTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
  catch { return ''; }
};

// Score → medal tier. Shared by the result hero and the history list chips.
const tierFor = (score) => {
  const s = Number(score) || 0;
  const tier = s >= 90 ? 'gold' : s >= 70 ? 'silver' : s >= 40 ? 'bronze' : 'wood';
  return {
    gold:   { bg: 'linear-gradient(135deg, #FBBF24, #F59E0B)', label: 'Excellent' },
    silver: { bg: 'linear-gradient(135deg, #94A3B8, #64748B)', label: 'Great work' },
    bronze: { bg: 'linear-gradient(135deg, #FB923C, #EA580C)', label: 'Solid effort' },
    wood:   { bg: 'linear-gradient(135deg, #78716C, #57534E)', label: 'Keep practising' },
  }[tier];
};

export default function QAView({ lessonId, lessonTitle, classId }) {
  // Quiz lifecycle
  const [stage, setStage] = useState('idle');
  const [hardness, setHardness] = useState('medium');
  const [style, setStyle] = useState('mcq');
  const [count, setCount] = useState(10);
  const [session, setSession] = useState(null);
  const [answers, setAnswers] = useState({});
  const [revealed, setRevealed] = useState({});  // qid -> { correct, correctAnswer, explanation }
  const [cursor, setCursor] = useState(0);
  const [streak, setStreak] = useState(0);
  const [maxStreak, setMaxStreak] = useState(0);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [credits, setCredits] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);

  // History (completed attempts for THIS lesson only)
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [openingId, setOpeningId] = useState(null);
  const [viewingHistory, setViewingHistory] = useState(false);

  // Side-state
  const [defaults, setDefaults] = useState(null);

  const loadHistory = React.useCallback(() => {
    setHistoryLoading(true);
    return api.get(`/student/lesson/${lessonId}/quiz/history`)
      .then(d => setHistory(d.attempts || []))
      .catch(() => setHistory([]))
      .finally(() => setHistoryLoading(false));
  }, [lessonId]);

  // Per-lesson (re)load: reset local quiz state, fetch credits/defaults/history,
  // then resume any unfinished attempt exactly where the student left off.
  useEffect(() => {
    let alive = true;
    setStage('idle'); setSession(null); setAnswers({}); setResult(null);
    setRevealed({}); setCursor(0); setStreak(0); setMaxStreak(0); setViewingHistory(false);

    // Quiz generation bills institution credits (real token cost), not
    // Super Credits — show the institution balance here.
    api.get('/student/credits/peek').then(d => alive && setCredits(d.institution_credits)).catch(() => {});
    api.get(`/student/lesson/${lessonId}/generation-defaults?kind=magicdesk`)
      .then(d => alive && setDefaults(d)).catch(() => {});
    loadHistory();
    // Resume in-progress attempt (if any)
    api.get(`/student/lesson/${lessonId}/quiz/active`).then(d => {
      if (!alive || !d.active) return;
      const a = d.active;
      setSession({ session_id: a.session_id, questions: a.questions, hardness: a.hardness });
      setAnswers(a.answers || {});
      setCursor(Math.max(0, Math.min((a.questions?.length || 1) - 1, a.cursor || 0)));
      setStage('playing');
    }).catch(() => {});
    return () => { alive = false; };
  }, [lessonId, loadHistory]);

  // Apply ML-tuned defaults to hardness on first load (don't override user picks)
  useEffect(() => {
    if (defaults?.hardness) setHardness(defaults.hardness);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaults?.hardness]);

  // ── Quiz generation
  const generateQuiz = async () => {
    setGenerating(true); setResult(null); setRevealed({}); setAnswers({});
    setStreak(0); setMaxStreak(0); setCursor(0);
    try {
      const r = await api.post(`/student/lesson/${lessonId}/quiz/generate`, { hardness, count, style });
      setSession({ session_id: r.session_id, questions: r.questions, hardness });
      setViewingHistory(false);
      if (typeof r.institution_credits_remaining === 'number') setCredits(r.institution_credits_remaining);
      if (r.newly_earned_badges?.length) celebrateBadges(r.newly_earned_badges);
      setStage('playing');
      toast.success(`${r.questions.length} questions ready!`);
    } catch (e) {
      // 402 = insufficient institution credits — the institution tops this
      // pool up, not the student, so there's nothing to "recharge" here.
      const status = e?.response?.status || e?.status;
      const detail = e?.response?.data?.detail || e?.detail;
      if (status === 402 && detail && typeof detail === 'object' && detail.pool === 'institution') {
        if (typeof detail.balance === 'number') setCredits(detail.balance);
        toast.error('Your institution is out of credits for quizzes. Ask them to top up.');
      } else {
        toast.error(e.message || 'Could not generate quiz');
      }
    } finally { setGenerating(false); }
  };

  // Persist progress on navigation (save-on-nav). Fire-and-forget; a failed
  // save must never interrupt the playthrough.
  const persistProgress = (ans, cur) => {
    if (!session?.session_id) return;
    api.patch(`/student/lesson/${lessonId}/quiz/${session.session_id}/progress`, { answers: ans, cursor: cur })
      .catch(() => {});
  };

  const goTo = (idx) => {
    if (!session) return;
    const clamped = Math.max(0, Math.min(session.questions.length - 1, idx));
    setCursor(clamped);
    persistProgress(answers, clamped);
  };

  // ── Live single-question lock-in (no submit-all; instant feedback)
  const lockAnswer = (q, value) => {
    if (revealed[q.id]) return;
    if (!value || running) return;
    // Optimistic streak: locked but not yet validated until submit; we still
    // give live feedback for MCQ by trusting the server's stored answer when
    // the final submit is called. To keep the playthrough exciting we let the
    // student SEE their pick stick, then continue; on Submit at the end the
    // server returns the truth and we replay each card with reveals.
    setAnswers(a => ({ ...a, [q.id]: value }));
  };

  const next = () => goTo(cursor + 1);
  const prev = () => goTo(cursor - 1);

  const submitQuiz = async () => {
    if (!session) return;
    setRunning(true); setStage('submitting');
    try {
      const r = await api.post(`/student/lesson/${lessonId}/quiz/submit`, { session_id: session.session_id, answers });
      setResult(r);
      setViewingHistory(false);
      setMaxStreak(r.max_streak || 0);
      const nextRevealed = {};
      r.feedback.forEach(f => { nextRevealed[f.id] = f; });
      setRevealed(nextRevealed);
      setStage('done');
      loadHistory();  // the just-finished attempt now appears in history
      if (r.score >= 80) {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 2400);
      }
      if (r.newly_earned_badges?.length) celebrateBadges(r.newly_earned_badges);
    } catch (e) {
      toast.error(e.message || 'Submit failed');
      setStage('playing');
    } finally { setRunning(false); }
  };

  // Re-open a past completed attempt into the same results screen.
  const openHistoryResult = async (sessionId) => {
    setOpeningId(sessionId);
    try {
      const r = await api.get(`/student/lesson/${lessonId}/quiz/${sessionId}/result`);
      setResult(r);
      const nextRevealed = {};
      r.feedback.forEach(f => { nextRevealed[f.id] = f; });
      setRevealed(nextRevealed);
      setSession(null);       // viewing history, not resuming a live attempt
      setViewingHistory(true);
      setStage('done');
      try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch { /* noop */ }
    } catch (e) {
      toast.error(e.message || 'Could not open result');
    } finally { setOpeningId(null); }
  };

  const restart = () => {
    setSession(null); setAnswers({}); setResult(null); setRevealed({});
    setCursor(0); setStreak(0); setMaxStreak(0); setViewingHistory(false); setStage('idle');
  };

  return (
    <div className="space-y-6 relative" data-testid="qa-view">
      {showConfetti && <Confetti />}

      <TeacherQA lessonId={lessonId} classId={classId} lessonTitle={lessonTitle} />

      {/* Quiz card — immersive hero with subtle gradient + single primary CTA.
          Advanced options (style, count) hide behind a discrete "Advanced"
          chevron so the default flow is just: pick hardness → tap Start. */}
      <section
        className="rounded-3xl overflow-hidden relative"
        style={{
          background: 'var(--st-card)',
          border: '1px solid var(--st-border)',
          boxShadow: '0 12px 40px -16px color-mix(in srgb, var(--st-primary) 22%, transparent), 0 2px 6px -2px rgba(15,23,42,0.04)',
        }}
        data-testid="qa-quiz-section">
        {/* Decorative orb — uses theme primary so it changes per theme. */}
        <div
          aria-hidden="true"
          className="absolute -top-16 -right-12 w-64 h-64 rounded-full opacity-[0.10] blur-3xl pointer-events-none"
          style={{ background: 'radial-gradient(circle, var(--st-primary), transparent 70%)' }}
        />
        <header className="px-6 pt-6 pb-4 flex items-start justify-between gap-3 relative">
          <div className="flex items-center gap-3 min-w-0">
            <div
              className="w-11 h-11 rounded-2xl text-white flex items-center justify-center shadow-md shrink-0"
              style={{ background: 'linear-gradient(135deg, var(--st-primary), var(--st-accent))' }}>
              <Sparkles className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h2
                className="text-[20px] sm:text-[22px] font-extrabold tracking-tight leading-tight"
                style={{ fontFamily: 'var(--st-font-heading)', color: 'var(--st-ink)' }}>
                Test what you&rsquo;ve learned
              </h2>
              <p className="text-[12px] mt-0.5 leading-snug" style={{ color: 'var(--st-mute)' }}>
                We&rsquo;ll craft a quiz on <b style={{ color: 'var(--st-ink)' }}>{lessonTitle || 'this lesson'}</b> — earn XP, streaks & badges.
              </p>
            </div>
          </div>
          <span
            className="inline-flex items-center gap-1 text-[11px] font-bold rounded-full px-2.5 py-1 shrink-0"
            style={{ background: 'var(--st-bg)', color: 'var(--st-ink)', border: '1px solid var(--st-border)' }}
            data-testid="qa-credits-chip"
            title="Institution credits balance">
            <Zap className="w-3 h-3" style={{ color: 'var(--st-accent)' }} />
            {credits == null ? '—' : credits}
          </span>
        </header>

        {stage === 'idle' && (
          <QuizSetup
            hardness={hardness} setHardness={setHardness}
            style={style} setStyle={setStyle}
            count={count} setCount={setCount}
            generating={generating}
            onStart={generateQuiz}
          />
        )}

        {stage === 'playing' && session && (
          <QuizPlay
            questions={session.questions}
            cursor={cursor}
            answers={answers}
            streak={streak}
            maxStreak={maxStreak}
            hardness={session.hardness || hardness}
            onPick={lockAnswer}
            onChangeShort={(qid, v) => setAnswers(a => ({ ...a, [qid]: v }))}
            onPrev={prev}
            onNext={next}
            onJump={goTo}
            onSubmit={submitQuiz}
            running={running}
          />
        )}

        {stage === 'submitting' && (
          <div className="px-5 py-12 text-center text-sm" style={{ color: 'var(--st-mute)' }}>
            <div className="inline-block w-8 h-8 border-2 rounded-full border-current border-r-transparent animate-spin" />
            <div className="mt-3 font-semibold">Scoring your answers…</div>
          </div>
        )}

        {stage === 'done' && result && (
          <QuizResult
            result={result}
            hardness={result.hardness || session?.hardness || hardness}
            viewingHistory={viewingHistory}
            onRestart={restart}
          />
        )}
      </section>

      {/* Quiz history — completed attempts for THIS lesson only. Hidden while
          actively playing/scoring so it doesn't distract from the run. */}
      {stage !== 'playing' && stage !== 'submitting' && (
        <QuizHistory
          attempts={history}
          loading={historyLoading}
          openingId={openingId}
          activeSessionId={result?.session_id}
          onOpen={openHistoryResult}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Teacher Q&A — scope tabs + lesson-grouped expansion
// ═══════════════════════════════════════════════════════════════════════════
function TeacherQA({ lessonId, classId, lessonTitle }) {
  const [scope, setScope] = useState('lesson');   // 'lesson' | 'class'
  const [pairs, setPairs] = useState([]);
  const [groups, setGroups] = useState([]);
  const [openLesson, setOpenLesson] = useState(0);
  const [openIdx, setOpenIdx] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    if (scope === 'lesson') {
      api.get(`/student/lesson/${lessonId}/qa/teacher`)
        .then(d => alive && setPairs(d.pairs || []))
        .catch(() => alive && setPairs([]))
        .finally(() => alive && setLoading(false));
    } else {
      api.get(`/student/class/${classId}/qa/all`)
        .then(d => { if (alive) { setGroups(d.groups || []); setOpenLesson(0); } })
        .catch(() => alive && setGroups([]))
        .finally(() => alive && setLoading(false));
    }
    return () => { alive = false; };
  }, [scope, lessonId, classId]);

  const totalPairs = scope === 'lesson' ? pairs.length : groups.reduce((n, g) => n + g.pairs.length, 0);

  return (
    <section
      className="rounded-3xl overflow-hidden relative"
      style={{
        background: 'var(--st-card)',
        border: '1px solid var(--st-border)',
        boxShadow: '0 12px 40px -18px color-mix(in srgb, var(--st-primary) 18%, transparent), 0 2px 6px -2px rgba(15,23,42,0.04)',
      }}
      data-testid="qa-teacher-section">
      <div
        aria-hidden="true"
        className="absolute -top-16 -left-12 w-56 h-56 rounded-full opacity-[0.08] blur-3xl pointer-events-none"
        style={{ background: 'radial-gradient(circle, var(--st-accent), transparent 70%)' }}
      />
      <header className="px-5 sm:px-6 pt-5 pb-3 flex items-center gap-2.5 flex-wrap relative">
        <div className="w-9 h-9 rounded-xl text-white flex items-center justify-center shadow-sm shrink-0" style={{ background: 'linear-gradient(135deg, var(--st-primary), var(--st-accent))' }}>
          <GraduationCap className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <h2 className="text-[15px] font-bold tracking-tight leading-tight" style={{ fontFamily: 'var(--st-font-heading)', color: 'var(--st-ink)' }}>Questions from your teacher</h2>
          <span className="text-[11px]" style={{ color: 'var(--st-mute)' }}>{totalPairs} Q{totalPairs === 1 ? '' : 's'}{scope === 'class' ? ' across this class' : ''}</span>
        </div>
        <div className="ml-auto flex gap-1 p-0.5 rounded-full" style={{ background: 'var(--st-bg)', border: '1px solid var(--st-border)' }}>
          {[
            { id: 'lesson', label: 'This lesson' },
            { id: 'class',  label: 'All class'  },
          ].map(t => {
            const a = scope === t.id;
            return (
              <button key={t.id} onClick={() => setScope(t.id)}
                data-testid={`qa-scope-${t.id}`}
                className="px-3 py-1 rounded-full text-[11px] font-bold transition-all"
                style={{ background: a ? 'linear-gradient(135deg, var(--st-primary), var(--st-accent))' : 'transparent', color: a ? '#fff' : 'inherit' }}>
                {t.label}
              </button>
            );
          })}
        </div>
      </header>

      <div className="px-5 sm:px-6 pb-5 relative">
        {loading ? (
          <div className="text-[12px] py-4" style={{ color: 'var(--st-mute)' }}>Loading Q&amp;A…</div>
        ) : scope === 'lesson' ? (
          pairs.length === 0
            ? <div className="text-[12px] py-4" style={{ color: 'var(--st-mute)' }}>No teacher Q&amp;A on <b style={{ color: 'var(--st-ink)' }}>{lessonTitle}</b>. Try the AI Quiz Arena below — or switch to <b>All class</b> for the full revision sheet.</div>
            : <PairList pairs={pairs} openIdx={openIdx} setOpenIdx={setOpenIdx} prefix="qa-teacher-lesson" />
        ) : groups.length === 0 ? (
          <div className="text-[12px] py-4" style={{ color: 'var(--st-mute)' }}>No teacher Q&amp;A across this class yet.</div>
        ) : (
          <ul className="space-y-2.5" data-testid="qa-class-groups">
            {groups.map((g, gi) => {
              const open = openLesson === gi;
              return (
                <li key={g.lesson_id} className="rounded-2xl border overflow-hidden transition-colors" style={{ borderColor: open ? 'var(--st-primary)' : 'var(--st-border)', background: 'var(--st-bg)' }} data-testid={`qa-class-group-${g.lesson_id}`}>
                  <button onClick={() => setOpenLesson(open ? -1 : gi)}
                    data-testid={`qa-class-toggle-${g.lesson_id}`}
                    className="w-full px-3.5 py-3 text-left flex items-center justify-between gap-2 hover:bg-black/[0.02] transition-colors">
                    <span className="inline-flex items-center gap-2.5 min-w-0">
                      <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'color-mix(in srgb, var(--st-primary) 14%, transparent)', color: 'var(--st-primary)' }}>
                        <BookOpen className="w-3.5 h-3.5" />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-[12.5px] font-bold truncate" style={{ fontFamily: 'var(--st-font-heading)' }}>{g.lesson_title}</span>
                        <span className="block text-[10px] opacity-70 mt-0.5">{fmtDate(g.published_at)} · {g.pairs.length} Q&amp;A</span>
                      </span>
                    </span>
                    <ChevronRight className={`w-4 h-4 opacity-50 transition-transform shrink-0 ${open ? 'rotate-90' : ''}`} />
                  </button>
                  {open && (
                    <div className="border-t px-3.5 py-3" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
                      <PairList pairs={g.pairs} openIdx={openIdx} setOpenIdx={setOpenIdx} prefix={`qa-class-${g.lesson_id}`} />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}

function PairList({ pairs, openIdx, setOpenIdx, prefix }) {
  return (
    <ul className="space-y-2">
      {pairs.map((p, i) => {
        const open = openIdx === `${prefix}-${i}`;
        return (
          <li key={i} className="rounded-xl border overflow-hidden transition-colors" style={{ borderColor: open ? 'var(--st-primary)' : 'var(--st-border)', background: 'var(--st-bg)' }}>
            <button onClick={() => setOpenIdx(open ? null : `${prefix}-${i}`)}
              data-testid={`${prefix}-item-${i}`}
              className="w-full text-left flex items-start gap-2.5 px-3 py-2.5 hover:bg-black/[0.02] transition-colors">
              <span className="mt-0.5 w-6 h-6 rounded-full text-[10px] font-extrabold text-white flex items-center justify-center shrink-0" style={{ background: 'linear-gradient(135deg, var(--st-primary), var(--st-accent))' }}>Q</span>
              <span className="flex-1 text-[13px] font-semibold leading-snug" style={{ fontFamily: 'var(--st-font-heading)' }}><MathText text={p.question} /></span>
              <ChevronRight className={`w-4 h-4 mt-1 opacity-50 transition-transform shrink-0 ${open ? 'rotate-90' : ''}`} />
            </button>
            {open && p.answer && (
              <div className="mx-3 mb-3 rounded-lg p-3 text-[12px] leading-relaxed flex items-start gap-2.5" style={{ background: 'var(--st-card)', border: '1px solid var(--st-border)' }}>
                <span className="mt-0.5 w-6 h-6 rounded-full text-[10px] font-extrabold text-white flex items-center justify-center shrink-0" style={{ background: 'var(--st-accent)' }}>A</span>
                <span style={{ color: 'var(--st-ink)' }}><MathText text={p.answer} /></span>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Quiz Setup screen
// ═══════════════════════════════════════════════════════════════════════════
function QuizSetup({ hardness, setHardness, style, setStyle, count, setCount, generating, onStart }) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const hardnessObj = HARDNESS.find(h => h.id === hardness) || HARDNESS[1];

  return (
    <div className="px-6 pb-6 space-y-5" data-testid="quiz-setup">
      {/* ── Primary: hardness segmented control + count slider ─────────── */}
      <div className="space-y-4">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] mb-2.5 flex items-center justify-between" style={{ color: 'var(--st-mute)' }}>
            <span>Difficulty</span>
            <span className="font-mono opacity-80">×{hardnessObj.mult} XP</span>
          </div>
          <div
            className="grid grid-cols-3 gap-0 rounded-2xl p-1"
            style={{ background: 'var(--st-bg)', border: '1px solid var(--st-border)' }}>
            {HARDNESS.map(h => {
              const a = hardness === h.id;
              return (
                <button key={h.id} onClick={() => setHardness(h.id)}
                  data-testid={`quiz-hardness-${h.id}`}
                  className="rounded-xl py-2 text-[12.5px] font-bold transition-all"
                  style={{
                    background: a ? 'linear-gradient(135deg, var(--st-primary), var(--st-accent))' : 'transparent',
                    color: a ? '#fff' : 'var(--st-ink)',
                    boxShadow: a ? '0 4px 12px -4px color-mix(in srgb, var(--st-primary) 50%, transparent)' : 'none',
                    fontFamily: 'var(--st-font-heading)',
                  }}>
                  {h.label}
                </button>
              );
            })}
          </div>
          <p className="text-[11px] mt-2 leading-snug" style={{ color: 'var(--st-mute)' }}>{hardnessObj.desc}</p>
        </div>

        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] mb-2.5 flex items-center justify-between" style={{ color: 'var(--st-mute)' }}>
            <span>Questions</span>
            <span className="font-mono" style={{ color: 'var(--st-ink)' }}>{count}</span>
          </div>
          <input
            type="range"
            min={5} max={20} step={5}
            value={count}
            onChange={e => setCount(Number(e.target.value))}
            data-testid="quiz-count-slider"
            className="w-full h-2 rounded-full appearance-none cursor-pointer"
            style={{
              background: `linear-gradient(to right, var(--st-primary) 0%, var(--st-primary) ${((count - 5) / 15) * 100}%, var(--st-bg) ${((count - 5) / 15) * 100}%, var(--st-bg) 100%)`,
              accentColor: 'var(--st-primary)',
            }}
          />
          <div className="flex justify-between mt-1.5 text-[10px] font-semibold" style={{ color: 'var(--st-mute)' }}>
            <span>5</span><span>10</span><span>15</span><span>20</span>
          </div>
        </div>
      </div>

      {/* ── Advanced (collapsed by default — just one click) ───────────── */}
      <details
        className="rounded-xl overflow-hidden group"
        open={showAdvanced}
        onToggle={(e) => setShowAdvanced(e.currentTarget.open)}
        style={{ border: '1px dashed var(--st-border)' }}>
        <summary
          className="px-3 py-2 text-[11px] font-semibold cursor-pointer select-none flex items-center justify-between list-none"
          style={{ color: 'var(--st-mute)' }}
          data-testid="quiz-advanced-toggle">
          <span className="inline-flex items-center gap-1.5">
            <Target className="w-3 h-3" />
            Advanced — question style ({STYLES.find(s => s.id === style)?.label})
          </span>
          <ChevronRight className="w-3.5 h-3.5 transition-transform group-open:rotate-90" />
        </summary>
        <div className="px-3 pb-3 pt-1">
          <div className="flex flex-wrap gap-1.5">
            {STYLES.map(s => (
              <button key={s.id} onClick={() => setStyle(s.id)}
                data-testid={`quiz-style-${s.id}`}
                className="px-3 py-1.5 rounded-full text-[11px] font-bold border transition-all"
                style={{
                  borderColor: style === s.id ? 'var(--st-primary)' : 'var(--st-border)',
                  background: style === s.id ? 'var(--st-primary)' : 'transparent',
                  color: style === s.id ? '#fff' : 'var(--st-ink)',
                }}>
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </details>

      {/* ── Single primary CTA — full-width, big, theme-aware ──────────── */}
      <button
        onClick={onStart}
        disabled={generating}
        data-testid="quiz-start"
        className="w-full rounded-2xl px-5 py-4 text-[15px] font-extrabold text-white inline-flex items-center justify-center gap-2 disabled:opacity-50 transition-all hover:scale-[1.01] active:scale-[0.99] shadow-lg"
        style={{
          background: 'linear-gradient(135deg, var(--st-primary), var(--st-accent))',
          fontFamily: 'var(--st-font-heading)',
          boxShadow: '0 10px 28px -10px color-mix(in srgb, var(--st-primary) 60%, transparent)',
        }}>
        {generating ? (
          <>
            <div className="w-4 h-4 border-2 rounded-full border-white border-r-transparent animate-spin" />
            Generating…
          </>
        ) : (
          <>
            <Play className="w-4 h-4 fill-white" /> Start {count}-question quiz
          </>
        )}
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Quiz Play — one card at a time, animated, with progress bar + streak HUD
// ═══════════════════════════════════════════════════════════════════════════
function QuizPlay({ questions, cursor, answers, streak, maxStreak, hardness, onPick, onChangeShort, onPrev, onNext, onJump, onSubmit, running }) {
  const q = questions[cursor];
  const answered = !!(answers[q?.id] && (answers[q.id] || '').trim());
  const allAnswered = questions.every(qq => (answers[qq.id] || '').trim());
  const progress = ((cursor + 1) / questions.length) * 100;

  return (
    <div className="px-5 py-5 space-y-4" data-testid="quiz-playing">
      {/* HUD */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--st-mute)' }}>
            Question <b style={{ color: 'var(--st-ink)' }}>{cursor + 1}</b> / {questions.length}
          </span>
          <span className="text-[11px] font-bold rounded-full px-2 py-0.5 inline-flex items-center gap-1 capitalize"
            style={{ background: 'var(--st-bg)', border: '1px solid var(--st-border)' }}>
            <Target className="w-3 h-3" style={{ color: 'var(--st-primary)' }} /> {hardness}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <Stat label="Answered" value={`${Object.values(answers).filter(v => (v || '').trim()).length}/${questions.length}`} />
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--st-bg)', border: '1px solid var(--st-border)' }} data-testid="quiz-progress">
        <div className="h-full transition-[width] duration-500 ease-out" style={{ width: `${progress}%`, background: 'linear-gradient(90deg, var(--st-primary), var(--st-accent))' }} />
      </div>

      {/* Card */}
      <div key={q.id} className="rounded-2xl border p-5 transition-all"
        style={{ borderColor: 'var(--st-border)', background: 'var(--st-bg)', animation: 'qa-card-in .25s ease-out' }}
        data-testid={`quiz-card-${q.id}`}>
        <div className="flex items-start gap-3">
          <span className="w-8 h-8 rounded-full text-xs font-extrabold text-white flex items-center justify-center shrink-0"
            style={{ background: 'var(--st-primary)' }}>{cursor + 1}</span>
          <div className="flex-1 min-w-0">
            <div className="text-base font-bold leading-snug" style={{ fontFamily: 'var(--st-font-heading)' }}><MathText text={q.question} /></div>
            <div className="mt-4">
              {q.type === 'mcq' ? (
                <div className="space-y-2">
                  {q.options.map((opt, oi) => {
                    const picked = answers[q.id] === opt;
                    return (
                      <button key={oi} onClick={() => onPick(q, opt)}
                        data-testid={`quiz-pick-${q.id}-${oi}`}
                        className="w-full text-left rounded-xl px-4 py-3 border-2 transition-all flex items-center gap-3"
                        style={{
                          borderColor: picked ? 'var(--st-primary)' : 'var(--st-border)',
                          background: picked ? 'var(--st-primary)' : 'var(--st-card)',
                          color: picked ? '#fff' : 'var(--st-ink)',
                        }}>
                        <span className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold border-2 shrink-0"
                          style={{ borderColor: picked ? '#fff' : 'var(--st-border)', background: picked ? 'rgba(255,255,255,.2)' : 'transparent' }}>
                          {String.fromCharCode(65 + oi)}
                        </span>
                        <span className="text-[13px] font-semibold"><MathText text={opt} /></span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <textarea value={answers[q.id] || ''}
                  onChange={e => onChangeShort(q.id, e.target.value)}
                  data-testid={`quiz-short-${q.id}`}
                  placeholder="Type your answer here…"
                  className="w-full rounded-xl px-3 py-2 text-[13px] resize-none outline-none"
                  rows={3}
                  style={{ background: 'var(--st-card)', border: '2px solid var(--st-border)', color: 'var(--st-ink)' }} />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Nav */}
      <div className="flex items-center justify-between gap-2">
        <button onClick={onPrev} disabled={cursor === 0}
          data-testid="quiz-prev"
          className="rounded-full px-4 py-2 text-xs font-bold border inline-flex items-center gap-1 disabled:opacity-30"
          style={{ borderColor: 'var(--st-border)' }}>
          <ChevronLeft className="w-3.5 h-3.5" /> Prev
        </button>
        <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--st-mute)' }}>
          {answered ? '✓ answer locked' : 'pick to continue'}
        </span>
        {cursor < questions.length - 1 ? (
          <button onClick={onNext}
            data-testid="quiz-next"
            className="rounded-full px-5 py-2 text-sm font-bold inline-flex items-center gap-1 transition-transform hover:scale-105"
            style={{ background: 'var(--st-primary)', color: '#fff' }}>
            Next <ChevronRight className="w-3.5 h-3.5" />
          </button>
        ) : (
          <button onClick={onSubmit} disabled={!allAnswered || running}
            data-testid="quiz-submit"
            className="rounded-full px-5 py-2 text-sm font-bold text-white disabled:opacity-50 inline-flex items-center gap-1 transition-transform hover:scale-105"
            style={{ background: '#16A34A' }}>
            {running ? 'Submitting…' : 'Submit quiz'} <Trophy className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Quick-jump strip */}
      <div className="flex flex-wrap gap-1 pt-2 border-t" style={{ borderColor: 'var(--st-border)' }}>
        {questions.map((qq, i) => {
          const a = i === cursor;
          const ans = !!(answers[qq.id] || '').trim();
          return (
            <button key={qq.id} onClick={() => onJump(i)}
              data-testid={`quiz-jump-${i}`}
              className="w-7 h-7 rounded-md text-[11px] font-bold transition-all"
              style={{
                background: a ? 'var(--st-primary)' : (ans ? 'var(--st-accent)' : 'var(--st-bg)'),
                color: a || ans ? '#fff' : 'inherit',
                border: '1px solid var(--st-border)',
              }}>
              {i + 1}
            </button>
          );
        })}
      </div>

      <style>{`
        @keyframes qa-card-in {
          from { opacity: 0; transform: translateY(8px) scale(.99); }
          to   { opacity: 1; transform: translateY(0)   scale(1); }
        }
      `}</style>
    </div>
  );
}

function Stat({ label, value, tint }) {
  return (
    <div className="text-right leading-tight">
      <div className="text-[9px] font-bold uppercase tracking-widest opacity-60">{label}</div>
      <div className="text-sm font-extrabold tabular-nums" style={{ color: tint || 'var(--st-ink)', fontFamily: 'var(--st-font-heading)' }}>{value}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Quiz Result — score reveal + per-question feedback + earned badges
// ═══════════════════════════════════════════════════════════════════════════
function QuizResult({ result, hardness, viewingHistory, onRestart }) {
  const tierColors = tierFor(result.score);
  return (
    <div className="px-5 py-5 space-y-5" data-testid="quiz-done">
      <div className="rounded-3xl p-6 text-white relative overflow-hidden" style={{ background: tierColors.bg }}>
        <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-white/15" />
        <div className="absolute right-12 -bottom-6 w-24 h-24 rounded-full bg-white/10" />
        <div className="relative flex items-center gap-4 flex-wrap">
          <Trophy className="w-10 h-10" />
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold opacity-80">{tierColors.label}</div>
            <div className="text-5xl font-extrabold leading-none mt-0.5" data-testid="quiz-score" style={{ fontFamily: 'var(--st-font-heading)' }}>{result.score}%</div>
            <div className="text-[12px] opacity-90 mt-1">{result.correct}/{result.total} correct · best streak <b>{result.max_streak || 0}</b> · {hardness}</div>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <Stat label="XP" value={`+${result.xp || 0}`} tint="#fff" />
            <button onClick={onRestart}
              data-testid="quiz-play-again"
              className="rounded-full px-4 py-2 text-xs font-bold inline-flex items-center gap-1 bg-white/20 backdrop-blur hover:bg-white/30">
              {viewingHistory
                ? <><ChevronLeft className="w-3.5 h-3.5" /> Back</>
                : <><RefreshCw className="w-3.5 h-3.5" /> Play again</>}
            </button>
          </div>
        </div>
      </div>

      {result.newly_earned_badges?.length > 0 && (
        <div className="rounded-2xl border p-4" style={{ borderColor: 'var(--st-accent)', background: 'var(--st-card)' }} data-testid="quiz-new-badges">
          <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--st-accent)' }}>New badges unlocked</div>
          <div className="flex flex-wrap gap-2 mt-2">
            {result.newly_earned_badges.map(b => (
              <div key={b.badge_id || b.id || b.name} className="rounded-xl border-2 px-3 py-2 inline-flex items-center gap-2"
                style={{ borderColor: 'var(--st-accent)', background: 'var(--st-bg)' }}
                data-testid={`quiz-badge-pop-${b.badge_id || b.name}`}>
                <span className="text-2xl">{b.icon || '🏅'}</span>
                <div className="leading-tight">
                  <div className="text-[12px] font-bold" style={{ fontFamily: 'var(--st-font-heading)' }}>{b.name || 'New badge'}</div>
                  <div className="text-[10px] opacity-70">{b.desc || ''}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <ol className="space-y-2.5">
        {result.feedback.map((f, i) => (
          <li key={f.id} className="rounded-xl p-4 border" style={{ borderColor: 'var(--st-border)', background: 'var(--st-bg)' }} data-testid={`quiz-feedback-${f.id}`}>
            <div className="flex items-start gap-2">
              <span className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                style={{ background: f.correct ? '#16A34A' : '#DC2626', color: '#fff' }}>
                {f.correct ? <Check className="w-3.5 h-3.5" /> : <XIcon className="w-3.5 h-3.5" />}
              </span>
              <div className="flex-1">
                <div className="text-sm font-semibold" style={{ fontFamily: 'var(--st-font-heading)' }}>{i + 1}. <MathText text={f.question} /></div>
                <div className="text-[12px] mt-2"><span className="opacity-60">Your answer:</span> <b><MathText text={f.your_answer || '—'} /></b></div>
                {!f.correct && <div className="text-[12px] mt-0.5"><span className="opacity-60">Correct:</span> <b style={{ color: '#16A34A' }}><MathText text={f.correct_answer} /></b></div>}
                {f.explanation && <div className="text-[12px] mt-2 opacity-80"><BookOpen className="w-3 h-3 inline mr-1" /> <MathText text={f.explanation} /></div>}
              </div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Quiz History — completed attempts for THIS lesson; click to reopen results
// ═══════════════════════════════════════════════════════════════════════════
function QuizHistory({ attempts, loading, openingId, activeSessionId, onOpen }) {
  if (loading) {
    return (
      <section className="rounded-3xl overflow-hidden" style={{ background: 'var(--st-card)', border: '1px solid var(--st-border)' }} data-testid="quiz-history-loading">
        <div className="px-5 py-4 text-[12px]" style={{ color: 'var(--st-mute)' }}>Loading quiz history…</div>
      </section>
    );
  }
  if (!attempts || attempts.length === 0) return null;

  return (
    <section className="rounded-3xl overflow-hidden" style={{ background: 'var(--st-card)', border: '1px solid var(--st-border)' }} data-testid="quiz-history">
      <header className="px-5 py-3 flex items-center gap-2">
        <div className="w-7 h-7 rounded-lg text-white flex items-center justify-center" style={{ background: 'var(--st-primary)' }}>
          <Trophy className="w-3.5 h-3.5" />
        </div>
        <h2 className="text-base font-bold tracking-tight" style={{ fontFamily: 'var(--st-font-heading)' }}>Your quiz history</h2>
        <span className="text-[11px]" style={{ color: 'var(--st-mute)' }}>{attempts.length} attempt{attempts.length === 1 ? '' : 's'}</span>
      </header>
      <ul className="px-3 pb-3 space-y-1.5">
        {attempts.map((a) => {
          const t = tierFor(a.score);
          const opening = openingId === a.session_id;
          const isActive = activeSessionId === a.session_id;
          return (
            <li key={a.session_id}>
              <button
                onClick={() => onOpen(a.session_id)}
                disabled={opening}
                data-testid={`quiz-history-item-${a.session_id}`}
                className="w-full rounded-xl px-3 py-2.5 flex items-center gap-3 text-left transition-all disabled:opacity-60 hover:brightness-105"
                style={{ background: isActive ? 'var(--st-bg)' : 'transparent', border: '1px solid var(--st-border)' }}>
                <span className="w-11 h-11 rounded-xl text-white flex items-center justify-center shrink-0" style={{ background: t.bg }}>
                  <span className="text-[13px] font-extrabold leading-none">{a.score ?? 0}%</span>
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[13px] font-bold capitalize" style={{ fontFamily: 'var(--st-font-heading)' }}>{a.hardness} · {a.total} Q{a.total === 1 ? '' : 's'}</span>
                  <span className="block text-[10.5px] opacity-70 mt-0.5">{fmtDateTime(a.submitted_at || a.created_at)} · best streak {a.max_streak || 0}</span>
                </span>
                {opening
                  ? <span className="w-4 h-4 border-2 rounded-full border-current border-r-transparent animate-spin opacity-60 shrink-0" />
                  : <ChevronRight className="w-4 h-4 opacity-50 shrink-0" />}
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Confetti — pure-CSS for high scores
// ═══════════════════════════════════════════════════════════════════════════
function Confetti() {
  const pieces = useMemo(() => {
    const colors = ['#F43F5E', '#F59E0B', '#10B981', '#3B82F6', '#A855F7', '#EC4899'];
    return Array.from({ length: 80 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 0.6,
      dur: 1.6 + Math.random() * 1.2,
      color: colors[i % colors.length],
      size: 6 + Math.random() * 8,
      rot: Math.random() * 360,
    }));
  }, []);
  return (
    <div className="pointer-events-none fixed inset-0 z-[80] overflow-hidden" data-testid="qa-confetti">
      {pieces.map(p => (
        <span key={p.id} className="absolute"
          style={{
            left: `${p.left}%`,
            top: '-5%',
            width: p.size, height: p.size * 1.4,
            background: p.color,
            transform: `rotate(${p.rot}deg)`,
            animation: `confetti-fall ${p.dur}s ${p.delay}s ease-in forwards`,
          }} />
      ))}
      <style>{`
        @keyframes confetti-fall {
          0%   { transform: translateY(-20px) rotate(0deg); opacity: 1; }
          80%  { opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Util: surface badge unlock toasts
// ═══════════════════════════════════════════════════════════════════════════
function celebrateBadges(list) {
  list.forEach((b, i) => {
    setTimeout(() => {
      toast.success(`${b.icon || '🏅'}  ${b.name || 'New badge'} — ${b.desc || ''}`);
    }, i * 700);
  });
}
